import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Event } from '../types/index.ts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, DollarSign, Users, TrendingUp, Calendar, Ticket } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export default function HostAnalytics() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (eventId) {
      loadAnalytics();
    }
  }, [eventId]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      const [eventData, analyticsData] = await Promise.all([
        api.getEvent(eventId!),
        api.getEventAnalytics(eventId!)
      ]);
      setEvent(eventData);
      setAnalytics(analyticsData);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !event || !analytics) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalRevenue = analytics.total_revenue || 0;
  const ticketsSold = analytics.tickets_sold || 0;
  const capacity = event.capacity || 0;
  const averageTicketPrice = ticketsSold > 0 ? totalRevenue / ticketsSold : 0;
  const soldPercentage = capacity > 0 ? (ticketsSold / capacity) * 100 : 0;

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/manage-events')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">{event.title}</h1>
          <p className="text-gray-400">Event Analytics</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold">${totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-lg bg-secondary/20 flex items-center justify-center">
                <Ticket className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Tickets Sold</p>
                <p className="text-2xl font-bold">{ticketsSold}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-lg bg-accent-cyan/20 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-accent-cyan" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Avg Ticket Price</p>
                <p className="text-2xl font-bold">${averageTicketPrice.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-lg bg-accent-lime/20 flex items-center justify-center">
                <Users className="h-6 w-6 text-accent-lime" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Capacity</p>
                <p className="text-2xl font-bold">{soldPercentage.toFixed(0)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales Progress */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Sales Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Tickets Sold</span>
            <span className="font-bold">{ticketsSold} / {capacity}</span>
          </div>
          <div className="h-4 bg-background-subtle rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-secondary transition-all"
              style={{ width: `${soldPercentage}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Ticket Tiers Breakdown */}
      {analytics.ticket_tiers && analytics.ticket_tiers.length > 0 && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle>Ticket Tiers Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.ticket_tiers.map((tier: any, index: number) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{tier.name}</span>
                    <span className="text-sm text-gray-400">
                      {tier.sold} / {tier.capacity} sold
                    </span>
                  </div>
                  <div className="h-2 bg-background-subtle rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${(tier.sold / tier.capacity) * 100}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">${tier.price} per ticket</span>
                    <span className="font-medium">${(tier.sold * tier.price).toFixed(2)} revenue</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Event Details */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Event Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-400">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">Event Date</span>
              </div>
              <p className="font-medium">{format(new Date(event.date), 'EEEE, MMMM dd, yyyy')}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-400">
                <Users className="h-4 w-4" />
                <span className="text-sm">Venue</span>
              </div>
              <p className="font-medium">{event.venue}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-400">
                <Ticket className="h-4 w-4" />
                <span className="text-sm">Category</span>
              </div>
              <p className="font-medium capitalize">{event.category}</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-400">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm">Status</span>
              </div>
              <p className="font-medium capitalize">{event.status}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attendee Demographics (Placeholder) */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Attendee Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-gray-400 py-8">
            Detailed attendee demographics coming soon
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
